import { Badge } from "./components/Badge";
import { Button } from "./components/Button";
import { getGuideBySlug } from "./data/marketplaceGuides";

function RatingIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
      <path d="m12 3.75 2.43 4.92 5.43.79-3.93 3.84.93 5.42L12 16.16l-4.86 2.56.93-5.42-3.93-3.84 5.43-.79L12 3.75Z" />
    </svg>
  );
}

function InstallIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden="true">
      <path
        d="M12 4.5v9m0 0 3.25-3.25M12 13.5 8.75 10.25M5.25 16.5h13.5"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DurationIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="7.5" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M12 8.25V12l3 1.75"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PreviewCard({ card }) {
  return (
    <article className="rounded-xl border border-kin-olive/12 bg-kin-white/84 p-4">
      <p className="text-[10px] font-semibold tracking-[0.12em] uppercase text-kin-muted">
        {card.label}
      </p>
      <p className="mt-1 text-3xl font-semibold text-kin-black">{card.value}</p>

      {card.spark ? (
        <div className="mt-3 flex h-9 items-end gap-1">
          {card.spark.map((value, index) => (
            <div key={index} className="flex-1 rounded-sm bg-kin-olive/10">
              <div className="w-full rounded-sm bg-kin-green/68" style={{ height: `${value}%` }} />
            </div>
          ))}
        </div>
      ) : null}

      {card.bars ? (
        <div className="mt-3 flex h-12 items-end gap-1">
          {card.bars.map((value, index) => (
            <div
              key={index}
              className="flex-1 rounded-sm bg-kin-green/65"
              style={{ height: `${value}%` }}
            />
          ))}
        </div>
      ) : null}

      {card.checklist ? (
        <div className="mt-3 space-y-1.5">
          {card.checklist.map((checked, index) => (
            <div key={index} className="flex items-center gap-2">
              <span
                className={`inline-flex h-3.5 w-3.5 rounded-sm border ${
                  checked
                    ? "border-kin-green/55 bg-kin-green/45"
                    : "border-kin-olive/18 bg-transparent"
                }`}
              />
              <span className="h-1.5 w-full rounded-full bg-kin-olive/12" />
            </div>
          ))}
        </div>
      ) : null}
    </article>
  );
}

function ActionItem({ action }) {
  return (
    <div className="rounded-lg border border-kin-olive/12 bg-kin-white/78 px-3 py-2">
      <p className="text-sm font-semibold text-kin-black">{action.title}</p>
      <p className="mt-1 text-sm text-kin-body/86">{action.text}</p>
    </div>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <article className="rounded-lg border border-kin-olive/12 bg-kin-white/82 px-3 py-3">
      <p className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-[0.11em] uppercase text-kin-muted">
        {icon}
        {label}
      </p>
      <p className="mt-1 text-sm font-semibold text-kin-black">{value}</p>
    </article>
  );
}

export default function MarketplaceGuidePage({ slug }) {
  const guide = getGuideBySlug(slug);

  if (!guide) {
    return (
      <div className="min-h-screen bg-kin-sunrise px-6 py-10 sm:px-8">
        <div className="mx-auto max-w-4xl rounded-xl border border-kin-olive/12 bg-kin-white/84 p-8 text-center">
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-kin-muted">
            Marketplace
          </p>
          <h1 className="mt-3 font-serif text-5xl font-bold text-kin-black">Guide not found</h1>
          <p className="mt-3 text-sm text-kin-body/88">
            The guide may have moved or no longer exists.
          </p>
          <a href="/private/design/marketplace" className="mt-6 inline-flex">
            <Button>Back to Directory</Button>
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-x-clip bg-kin-sunrise">
      <div className="pointer-events-none fixed inset-0 -z-20 bg-kin-sunrise animate-drift" />
      <div className="pointer-events-none fixed -left-20 top-0 -z-10 h-[26rem] w-[26rem] rounded-full bg-kin-lavender/12 blur-3xl" />
      <div className="pointer-events-none fixed right-[-7rem] top-20 -z-10 h-[22rem] w-[22rem] rounded-full bg-kin-gold/10 blur-3xl" />

      <header className="px-6 pt-6 sm:px-8">
        <div className="mx-auto flex max-w-6xl items-center justify-between rounded-xl border border-kin-olive/12 bg-kin-white/80 px-4 py-3 backdrop-blur-md sm:px-5">
          <a
            href="/private/design/marketplace"
            className="text-sm font-semibold text-kin-body hover:text-kin-green-dark"
          >
            ← Back to Marketplace
          </a>
          <Badge className="normal-case tracking-normal">{guide.category}</Badge>
        </div>
      </header>

      <main className="px-6 pb-16 pt-10 sm:px-8 sm:pb-20 sm:pt-14">
        <section className="mx-auto max-w-6xl">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1.2fr)_auto] lg:items-end">
            <div className="max-w-3xl">
              <h1 className="font-serif text-5xl font-bold leading-[0.98] text-kin-black sm:text-6xl">
                {guide.name}.
              </h1>
              <p className="mt-4 text-lg leading-relaxed text-kin-body/92">{guide.description}</p>
            </div>

            <div className="space-y-3">
              <a
                href={`mailto:sverre@kin.health?subject=Install%20Guide%20-%20${encodeURIComponent(guide.name)}`}
                className="block"
              >
                <Button size="large">Install Guide</Button>
              </a>
              <div className="grid gap-2 sm:grid-cols-3 lg:grid-cols-1">
                <StatCard icon={<RatingIcon />} label="Rating" value={`${guide.rating}/5`} />
                <StatCard icon={<InstallIcon />} label="Installs" value={guide.installs} />
                <StatCard icon={<DurationIcon />} label="Runtime" value={guide.duration} />
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto mt-7 grid max-w-6xl gap-4 xl:grid-cols-[0.82fr_1.18fr]">
          <article className="kin-surface rounded-xl p-5 sm:p-6">
            <p className="text-2xl font-semibold text-kin-black">About this Guide</p>
            <p className="mt-3 text-sm leading-relaxed text-kin-body/90">{guide.about}</p>
            <p className="mt-3 text-sm leading-relaxed text-kin-body/90">{guide.details}</p>

            <p className="mt-6 text-lg font-semibold text-kin-black">What You Can Do</p>
            <div className="mt-3 space-y-2">
              {guide.actions.map((action) => (
                <ActionItem key={action.title} action={action} />
              ))}
            </div>
          </article>

          <div className="space-y-4">
            <article className="kin-surface rounded-xl p-5 sm:p-6">
              <p className="text-2xl font-semibold text-kin-black">Guide Preview</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {guide.previewCards.map((card) => (
                  <PreviewCard key={card.label} card={card} />
                ))}
              </div>
            </article>

            <article className="kin-surface rounded-xl p-5 sm:p-6">
              <p className="text-[11px] font-semibold tracking-[0.14em] uppercase text-kin-muted">
                Created by
              </p>
              <p className="mt-1 text-xs font-semibold tracking-[0.12em] uppercase text-kin-green-dark">
                ✓ {guide.creatorBadge}
              </p>
              <p className="mt-2 text-3xl font-semibold text-kin-black">{guide.creator}</p>
              <p className="mt-2 text-sm leading-relaxed text-kin-body/90">{guide.creatorBio}</p>

              <p className="mt-4 text-[11px] font-semibold tracking-[0.14em] uppercase text-kin-muted">
                Required data
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {guide.requiredData.map((item) => (
                  <Badge key={item} variant="lavender" className="normal-case tracking-normal">
                    {item}
                  </Badge>
                ))}
              </div>
            </article>
          </div>
        </section>

        <section className="mx-auto mt-4 max-w-6xl">
          <article className="kin-surface rounded-xl p-5 sm:p-6">
            <p className="text-2xl font-semibold text-kin-black">Ratings and Reviews</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-[170px_1fr_1fr]">
              <div className="rounded-xl border border-kin-olive/12 bg-kin-white/84 p-4">
                <p className="font-serif text-6xl leading-none text-kin-black">{guide.rating}</p>
                <p className="mt-1 text-sm text-kin-muted">/5 average rating</p>
                <p className="mt-2 inline-flex items-center gap-1 text-sm text-kin-gold-dark">
                  <RatingIcon />
                  <RatingIcon />
                  <RatingIcon />
                  <RatingIcon />
                  <RatingIcon />
                </p>
              </div>

              {guide.reviews.map((review) => (
                <div
                  key={review.author}
                  className="rounded-xl border border-kin-olive/12 bg-kin-white/84 p-4"
                >
                  <p className="text-sm font-semibold text-kin-black">"{review.quote}"</p>
                  <p className="mt-2 text-sm text-kin-muted">— {review.author}</p>
                </div>
              ))}
            </div>
          </article>
        </section>
      </main>
    </div>
  );
}
